import { Component, inject, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { CaseService } from '../../cases/case.service';
import { FileService } from '../../file.service';
import { DialogType, MediDialogComponent } from '../../medi-dialog/medi-dialog.component';
import { OroService } from '../../oroservice';
import { DialogAbsenceComponent } from '../dialog-absence/dialog-absence.component';

export interface DialogData {
    frage: any;
}

@Component({
    selector: 'app-dialog-query',
    templateUrl: './dialog-query.component.html',
    styleUrls: ['./dialog-query.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class DialogQueryComponent implements OnInit {
    private readonly caseService = inject(CaseService);
    dossierInfo: any;
    isUpdating: boolean = false;

    constructor(
        public dialogRef: MatDialogRef<DialogAbsenceComponent>,
        public oroservice: OroService,
        public fileservice: FileService,
        public dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: DialogData) {
            fileservice.removeAllFiles();
        }

    async ngOnInit(): Promise<void> {
        const loadedCase = await this.caseService.getCase(this.data.frage.DossierNr);
        if (loadedCase != null) {
            this.dossierInfo = loadedCase;

            if (this.data.frage?.Art === 24) {
                this.data.frage?.Rechnungen.forEach(element => {
                    element.Betrag = null;
                });
                this.data.frage.Meldedatum = new Date();
            }

            this.data.frage.DirektzahlungsbetragTRV = null;

            // Falls es sich um eine Rückfrage Direktzahlung handelt und keine Antwortoptionen übergeben wurden,
            // dann ist die vorbelegte Antwort "Ich habe einen anderen Betrag/Zahldatum als aufgeführt erhalten" fest vorbelegt.
            // Dies benötigen wir für den Fall der Rückfrage mit fehlenden Angaben.
            if (this.data.frage?.Art === 12 && (!this.data.frage?.Texte.AnswersDE || this.data.frage?.Texte.AnswersDE.length === 0)) {
                this.data.frage.Antwort = 3;
            }
        } else {
            // TODO
        }
    }


    getCaptionAnswerDirektzahlungTRV(item: any) {
        const retVal: string[] = [];
        if (item == null) return retVal;

        if (this.oroservice.settingscurrent === this.oroservice.settingsit) {
            retVal.push(item.AnswersIT[0]);
            retVal.push(item.AnswersIT[1]);
            retVal.push(item.AnswersIT[2]);
            return retVal;
        }
        if (this.oroservice.settingscurrent === this.oroservice.settingsde) {
            retVal.push(item.AnswersDE[0]);
            retVal.push(item.AnswersDE[1]);
            retVal.push(item.AnswersDE[2]);
            return retVal;

        }
        if (this.oroservice.settingscurrent === this.oroservice.settingsfr) {
            retVal.push(item.AnswersFR[0]);
            retVal.push(item.AnswersFR[1]);
            retVal.push(item.AnswersFR[2]);
            return retVal;
        }
    }

    getCaptionOptionDirektzahlungTRV(item: any) {
        const retVal: string[] = [];
        if (item == null) return retVal;
        if (this.oroservice.settingscurrent === this.oroservice.settingsit) {
            retVal.push(item.AnswersIT[3]);
            retVal.push(item.AnswersIT[4]);
            retVal.push(item.AnswersIT[5]);
            return retVal;
        }
        if (this.oroservice.settingscurrent === this.oroservice.settingsde) {
            retVal.push(item.AnswersDE[3]);
            retVal.push(item.AnswersDE[4]);
            retVal.push(item.AnswersDE[5]);
            return retVal;

        }
        if (this.oroservice.settingscurrent === this.oroservice.settingsfr) {
            retVal.push(item.AnswersFR[3]);
            retVal.push(item.AnswersFR[4]);
            retVal.push(item.AnswersFR[5]);
            return retVal;
        }
    }

    checkDirektzahlungsDatumZukunft(datum: Date) {
        if (!datum) {
            return false;
        }
        return new Date().setHours(0, 0, 0, 0) < new Date(datum).setHours(0, 0, 0, 0);
    }

    hasDirektZahlung(): boolean {
        // DirektZahlung
        if (this.data.frage?.Art === 12 || this.data.frage?.Art === 26 || this.data.frage?.Art === 27) {
            return (this.data.frage.Antwort === 3);
        }

        // Zahlungseingang
        if (this.data.frage?.Art === 18) {
            return (this.data.frage.Antwort === 1);
        }

        // DirektZahlung Zustandigkeit
        if (this.data.frage?.Art === 24) {
            return (this.data.frage.Antwort === 1);
        }
        return false;
    }

    openDialog(paramWidth: string, paramTitle: string, paramContent: string, paramYes: string, paramNo: string, paramType: DialogType = DialogType.None): Observable<boolean> {
        const dialogRef = this.dialog.open(MediDialogComponent, {
            width: paramWidth,
            data: { title: paramTitle, content: paramContent, yes: paramYes, no: paramNo, type: paramType }
        });

        return dialogRef.afterClosed();
    }

    answer() {
        if (this.isUpdating) {
            return;
        }

        this.isUpdating = true;

        let formdata = this.fileservice.getfiles();

        const send = this.oroservice.clone(this.data.frage);

        // Adressrecherche: wenn der Anwender ins Ausland verzogen ist, in dem kein Inkasso betrieben wird,
        // wird das Dossier abgerechnet.
        if (this.data.frage.Antwort === 1 && this.data.frage.ausland === true) {
            send.Antwort = 4;
        }

        // Reklamation Rechnungsempfänger = Amt / Versicherung
        // Im Zuge von OR-3612 in VorgangReklamation.cs umgezogen
        /*
        if (!this.data.frage.ReAltPerson && this.data.frage.Art == 13 && this.data.frage.Reklamationsgrund == 3) {
            send.Antwort++;
        }
        */

        // Vertreter Nachhalten Behörde => Standardwert für Amtart setzen
        // wenn (Nachfrage = "Rückfrage Vertreter nachhalten (16)" und Beziehung = "Beistand (23)")
        // dann Amtart = "Sozialamt (8)"
        if (this.data.frage?.Art === 16 && this.data.frage.Gp.Beziehung === 23) {
            // TODO Rückfrage bei schweizer Kollegen ob Sozialamt hier richtig ist.
            this.data.frage.Gp.Amtart = 8 // Sozialamt
        }

        send.Texte = null;
        const json = JSON.stringify(send);
        formdata.append("antwort", json);
        this.oroservice.setAntwort(formdata).then((object) => {
            if (object != null && object.status === 400) {
                this.dialogRef.close(true);
            } else {
                this.fileservice.files = [];
                this.dialogRef.close(true);
            }

            this.isUpdating = false;

            this.openDialog("500px", "Meldung", "InfoStandardFeedback", "OK", "", DialogType.Info);
        });
    }


    getBetrag() {
        let betrag: number = 0;
        if (this.dossierInfo.Auftraege.length > 0) {
            betrag = this.dossierInfo.Auftraege.map(a => a.Betrag).reduce(function (a, b) { return +a + +b; });
        }

        return betrag;
    }

    getReferenz(): string {
        let ref: string = "";
        if (this.dossierInfo.Auftraege.length > 0) {
            ref = this.dossierInfo.Auftraege.map(a => a.Referenz).reduce(function (a, b) { return a + ', ' + b; });
        }

        return ref;
    }

    getKundennr(): string {
        let retVal: string = "";
        if (this.dossierInfo.Auftraege.length > 0) {
            this.dossierInfo.Auftraege.forEach(element => {
                if (element.ReKundennr) {
                    retVal = element.ReKundennr;
                }
            });
        }

        return retVal;
    }

    toLand(id: number): string {
        let retVal = "CH";
        switch (id) {
            case 1: {
                retVal = "FL";
                break;
            }
            case 2: {
                retVal = "DE";
                break;
            }
            case 3: {
                retVal = "FR";
                break;
            }
            case 4: {
                retVal = "AUT";
                break;
            }
        }

        return retVal;
    }

    getSchuldner() {
        let schuldner = this.dossierInfo.Rechnungsempfaenger;
        if (this.dossierInfo.StandortRE) {
            schuldner += this.dossierInfo.StandortRE.Anschrift ? ", " + this.dossierInfo.StandortRE.Anschrift : "";
            schuldner += ", " + this.toLand(this.dossierInfo.StandortRE.Land) + " " + this.dossierInfo.StandortRE.Plz + " " + this.dossierInfo.StandortRE.Ort;
        }

        return schuldner;
    }

    direktZahlungAdd(ford: any, index: number) {
        let forderungAdd: any = {};
        forderungAdd = {};
        forderungAdd.Rechnungsnummer = ford.Rechnungsnummer;
        forderungAdd.Id = ford.Id;

        this.data.frage.Rechnungen.splice(index + 1, 0, forderungAdd);
    }

    direktZahlungRemove(index: number) {
        this.data.frage.Rechnungen.splice(index, 1);
    }

    canSubmit() : boolean {
        let ok: boolean = true;

        if (this.data.frage?.Art === 21) {
            ok = this.fileservice.files.length > 0 && !this.fileservice.isFileTooBig;
        }

        if (this.hasDirektZahlung())
        {
            this.data.frage?.Rechnungen.forEach(element => {
                if (element.Betrag == null || element.Zahldatum == null || element.Betrag <= 0 || this.checkDirektzahlungsDatumZukunft(element.Zahldatum)) {
                    ok = false;
                }
            });
        }

        // Vertreter Nachhalten
        if (this.data.frage?.Art === 16) {
            if (this.data.frage.Antwort === 1) {
                if (this.data.frage.Gp.Beziehung < 20) {
                    ok = false;
                } else {
                    ok = ok && this.data.frage.Gp.Name && this.data.frage.Gp.Name.length >= 2;
                    ok = ok && this.data.frage.Gp.Adresse && this.data.frage.Gp.Adresse.length >= 2 && this.data.frage.Gp.Hausnummer && this.data.frage.Gp.Plz && this.data.frage.Gp.Ort;

                    if (this.data.frage.Gp.Beziehung === 23) {
                        ok = ok && true; // && this.data.frage.Gp.Amtart && this.data.frage.Gp.Amtart > 0;
                    } else {
                        ok = ok && this.data.frage.Gp.Vorname && this.data.frage.Gp.Vorname.length >= 2 && this.data.frage.Gp.Anrede;
                    }
                }
            }

        }

        // Reklamation
        if (this.data.frage?.Art === 13) {
            // Reklamation RE
            if (this.data.frage?.Reklamationsgrund === 3) {
                if (this.data.frage?.Antwort === 4) {
                    // neue RE validieren
                    if (!this.data.frage.Gp.Anrede || !this.data.frage.Gp.Vorname || !this.data.frage.Gp.Name || !this.data.frage.Gp.Adresse || !this.data.frage.Gp.Hausnummer || !this.data.frage.Gp.Ort)
                    {
                        ok = false;
                    }
                }
            }

            if (this.data.frage?.Reklamationsgrund === 2 && this.data.frage?.Antwort === 3) {
                if (this.data.frage?.Rechnungen?.some(rechnung => rechnung.Betrag < 0)) {
                    ok = false;
                }
            }
        }

        // Direktzahlung TRV
        if (this.data.frage?.Art === 29) {
            if (this.data.frage?.Antwort === 3 && this.data.frage?.DirektzahlungsbetragTRV <= 0) {
                ok = false;
            }

            if (this.data.frage?.Antwort === 2 && !this.data.frage.Antwort) {
                ok = false;
            }
        }

        // Rückfrage Adressrecherche
        if (this.data.frage?.Art === 11) {

            if (this.data.frage?.Antwort === 1) {
                // Adresse ist bekannt -> Adresse validieren

                 if (!this.data.frage.Standort.Anschrift && !this.data.frage.Standort.Postfach || !this.data.frage.Standort.Ort) {
                    ok = false;
                }
            }
        }

        return ok && (this.data.frage?.Art === 21 || this.data.frage.Antwort);
    }
}
